"use client";

import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date/DateTimePicker";
import { Input } from "@/components/ui/input";
import { Sparkles, Calendar } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Control } from "react-hook-form";
import type { CommunicationFormValues } from "./communicationFormSchema";

interface FollowUpSectionProps {
  control: Control<CommunicationFormValues>;
  showFollowUp: boolean;
  onToggle: (checked: boolean) => void;
  isSuggesting: boolean;
  isSubmitting: boolean;
}

export function FollowUpSection({
  control,
  showFollowUp,
  onToggle,
  isSuggesting,
  isSubmitting,
}: FollowUpSectionProps) {
  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="follow-up-toggle" className="text-base font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-500" />
            Next Step: Schedule Follow-up
          </Label>
          <p className="text-sm text-muted-foreground">
            Automatically create a task linked to this contact
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuggesting && (
            <div className="flex items-center gap-1.5 text-xs text-blue-600 animate-pulse bg-blue-50 px-2 py-1 rounded-full border border-blue-100 dark:text-blue-400 dark:bg-blue-950/40 dark:border-blue-800">
              <Sparkles className="h-3 w-3 animate-spin" />
              <span>AI Suggesting...</span>
            </div>
          )}
          <Switch
            id="follow-up-toggle"
            checked={showFollowUp}
            onCheckedChange={onToggle}
            disabled={isSubmitting}
          />
        </div>
      </div>

      {showFollowUp && (
        <Card className="bg-slate-50/50 border-dashed border-2 dark:bg-gray-900/50">
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="follow_up_due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Follow-up Date</FormLabel>
                    <FormControl>
                      <DateTimePicker value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="follow_up_note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quick Note</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Call back to discuss pricing"
                        {...field}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
